import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api.js";
import { Badge, Button, Card, COLORS, Empty, Field, Form, Input, Page, Textarea } from "../ui.js";

interface LessonRef {
  id: string;
  title: string;
  position: number;
  durationSec: number | null;
  updatedAt: string;
}

interface Module {
  id: string;
  title: string;
  description: string | null;
  position: number;
  lessons: LessonRef[];
}

interface Course {
  id: string;
  espaceId: string;
  slug: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  coverUrl: string | null;
  isPublished: boolean;
  modules: Module[];
}

export default function CourseDetail() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const nav = useNavigate();

  const { data: course, isLoading } = useQuery({
    queryKey: ["course", id],
    queryFn: () => api.get<Course>(`/courses/${id}`),
    enabled: !!id,
  });

  const update = useMutation({
    mutationFn: (patch: Partial<Course>) => api.patch<Course>(`/courses/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["course", id] }),
  });

  const del = useMutation({
    mutationFn: () => api.del(`/courses/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["courses"] });
      nav("/courses");
    },
  });

  const createModule = useMutation({
    mutationFn: (title: string) =>
      api.post<Module>(`/courses/${id}/modules`, { title }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["course", id] }),
  });

  if (isLoading) return <Empty>Chargement…</Empty>;
  if (!course) return <Empty>Cours introuvable.</Empty>;

  return (
    <Page title={course.title}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 16, flexWrap: "wrap" }}>
        <Badge kind={course.isPublished ? "ok" : "neutral"}>
          {course.isPublished ? "Publié" : "Brouillon"}
        </Badge>
        <span style={{ color: COLORS.muted, fontFamily: "monospace", fontSize: 13 }}>/{course.slug}</span>
        <div style={{ flex: 1 }} />
        <Link to="/courses" style={{ fontSize: 13, color: COLORS.muted, textDecoration: "none" }}>
          ← Tous les cours
        </Link>
        <Button variant="ghost" onClick={() => update.mutate({ isPublished: !course.isPublished })}>
          {course.isPublished ? "Dépublier" : "Publier"}
        </Button>
        <Button
          variant="danger"
          onClick={() => {
            if (confirm(`Supprimer "${course.title}" et tous ses modules ?`)) del.mutate();
          }}
        >
          Supprimer
        </Button>
      </div>

      <CourseMeta course={course} onChange={(p) => update.mutate(p)} />

      <h3 style={{ marginTop: 36, fontSize: 22 }}>Structure</h3>
      {course.modules.length === 0 && <Empty>Aucun Module. Ajoutez le premier.</Empty>}

      {course.modules.map((m) => (
        <ModuleCard key={m.id} courseId={course.id} module={m} />
      ))}

      <AddModuleForm onSubmit={(title) => createModule.mutate(title)} />
    </Page>
  );
}

// ─────────────────────────────────────────────────────────────
function CourseMeta({ course, onChange }: { course: Course; onChange: (p: Partial<Course>) => void }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(course.title);
  const [subtitle, setSubtitle] = useState(course.subtitle ?? "");
  const [description, setDescription] = useState(course.description ?? "");
  const [coverUrl, setCoverUrl] = useState(course.coverUrl ?? "");

  if (!editing) {
    return (
      <Card>
        <div style={{ display: "flex", gap: 20, alignItems: "start" }}>
          {course.coverUrl && (
            <img
              src={course.coverUrl}
              alt=""
              style={{ width: 160, height: 110, objectFit: "cover", borderRadius: 8 }}
            />
          )}
          <div style={{ flex: 1 }}>
            {course.subtitle && (
              <div style={{ color: COLORS.muted, fontSize: 15, marginBottom: 6 }}>{course.subtitle}</div>
            )}
            {course.description && (
              <div style={{ whiteSpace: "pre-wrap", color: COLORS.text, fontSize: 14, lineHeight: 1.6 }}>
                {course.description}
              </div>
            )}
            <div style={{ marginTop: 12 }}>
              <Button variant="ghost" onClick={() => setEditing(true)} style={{ padding: "6px 12px", fontSize: 13 }}>
                Modifier
              </Button>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <Field label="Titre">
        <Input value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <Field label="Sous-titre">
        <Input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} />
      </Field>
      <Field label="Description">
        <Textarea value={description} onChange={(e) => setDescription(e.target.value)} style={{ minHeight: 100 }} />
      </Field>
      <Field label="URL du cover (image)">
        <Input value={coverUrl} onChange={(e) => setCoverUrl(e.target.value)} placeholder="https://…" />
      </Field>
      <div style={{ display: "flex", gap: 8 }}>
        <Button
          onClick={() => {
            onChange({
              title,
              subtitle: subtitle || null,
              description: description || null,
              coverUrl: coverUrl || null,
            });
            setEditing(false);
          }}
        >
          Enregistrer
        </Button>
        <Button variant="ghost" onClick={() => setEditing(false)}>Annuler</Button>
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
function ModuleCard({ courseId, module: m }: { courseId: string; module: Module }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(m.title);

  const update = useMutation({
    mutationFn: (patch: Partial<Module>) => api.patch<Module>(`/courses/modules/${m.id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["course", courseId] }),
  });
  const del = useMutation({
    mutationFn: () => api.del(`/courses/modules/${m.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["course", courseId] }),
  });
  const addLesson = useMutation({
    mutationFn: (title: string) => api.post<LessonRef>(`/lessons/modules/${m.id}`, { title }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["course", courseId] }),
  });

  return (
    <Card>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span
          style={{
            background: COLORS.accent,
            color: "#fff",
            borderRadius: 6,
            padding: "4px 10px",
            fontWeight: 700,
            fontSize: 12,
          }}
        >
          M{m.position}
        </span>
        {!editing ? (
          <>
            <div style={{ fontSize: 17, fontWeight: 600, flex: 1 }}>{m.title}</div>
            <Button variant="ghost" onClick={() => setEditing(true)} style={{ padding: "4px 10px", fontSize: 12 }}>
              Renommer
            </Button>
          </>
        ) : (
          <>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} style={{ flex: 1 }} />
            <Button
              onClick={() => {
                update.mutate({ title });
                setEditing(false);
              }}
              style={{ padding: "4px 10px", fontSize: 12 }}
            >
              OK
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)} style={{ padding: "4px 10px", fontSize: 12 }}>
              Annuler
            </Button>
          </>
        )}
        <Button
          variant="danger"
          onClick={() => {
            if (confirm(`Supprimer le module "${m.title}" et toutes ses leçons ?`)) del.mutate();
          }}
          style={{ padding: "4px 10px", fontSize: 12 }}
        >
          ✕
        </Button>
      </div>

      {m.description && (
        <div style={{ color: COLORS.muted, fontSize: 14, marginTop: 8 }}>{m.description}</div>
      )}

      <div style={{ marginTop: 16, paddingLeft: 36 }}>
        {m.lessons.length === 0 && (
          <div style={{ fontSize: 13, color: COLORS.muted, fontStyle: "italic", marginBottom: 12 }}>
            Aucune leçon dans ce module.
          </div>
        )}
        {m.lessons.map((l) => (
          <div
            key={l.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: "8px 0",
              borderBottom: `1px solid ${COLORS.border}`,
            }}
          >
            <span style={{ color: COLORS.muted, fontSize: 12, minWidth: 24 }}>L{l.position}</span>
            <Link
              to={`/lessons/${l.id}/edit`}
              style={{ flex: 1, color: COLORS.text, textDecoration: "none", fontSize: 14 }}
            >
              {l.title}
            </Link>
          </div>
        ))}
        <AddLessonForm onSubmit={(title) => addLesson.mutate(title)} />
      </div>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
function AddModuleForm({ onSubmit }: { onSubmit: (title: string) => void }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");

  function onSub(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    onSubmit(title.trim());
    setTitle("");
    setOpen(false);
  }

  if (!open) {
    return (
      <Button onClick={() => setOpen(true)} style={{ marginTop: 16 }}>
        + Ajouter un Module
      </Button>
    );
  }
  return (
    <Card>
      <Form onSubmit={onSub}>
        <Field label="Titre du Module">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus />
        </Field>
        <div style={{ display: "flex", gap: 8 }}>
          <Button type="submit">Créer</Button>
          <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
        </div>
      </Form>
    </Card>
  );
}

function AddLessonForm({ onSubmit }: { onSubmit: (title: string) => void }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");

  function onSub(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    onSubmit(title.trim());
    setTitle("");
    setOpen(false);
  }

  if (!open) {
    return (
      <Button
        variant="ghost"
        onClick={() => setOpen(true)}
        style={{ marginTop: 8, padding: "6px 12px", fontSize: 13 }}
      >
        + Leçon
      </Button>
    );
  }
  return (
    <form onSubmit={onSub} style={{ display: "flex", gap: 8, marginTop: 8 }}>
      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titre" autoFocus />
      <Button type="submit" style={{ padding: "6px 14px", fontSize: 13 }}>Créer</Button>
      <Button variant="ghost" onClick={() => setOpen(false)} style={{ padding: "6px 14px", fontSize: 13 }}>
        ✕
      </Button>
    </form>
  );
}
